import { gpsDriveGet } from "@/lib/api/gpsdrive-client";
import { apiRequest } from "@/lib/api/client";
import type { User } from "@/types";

// Snapshot pra tela de Usuários nascer com dado real no F5/login, mesmo
// padrão/motivo do gpsdrive_vehicle_list_snapshot_cache_2026-09-04 em
// vehicles.service.ts (usado como `initialData` em useUsers(), ver
// hooks/queries/index.ts) — aqui a lista é minúscula (5 usuários reais),
// então nem a preocupação de tamanho/cota daquele caso se aplica. Escrito a
// cada `list()` bem-sucedido, inclusive o refetch disparado por
// invalidateQueries depois de criar/editar/excluir usuário — assim o
// snapshot nunca fica desatualizado em relação à última edição.
const USERS_LIST_CACHE_KEY = "gpsdrive.usersList.cache.v1";

export function readUsersListCache(): User[] | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(USERS_LIST_CACHE_KEY);
    return raw ? (JSON.parse(raw) as User[]) : undefined;
  } catch {
    return undefined;
  }
}

function writeUsersListCache(list: User[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(USERS_LIST_CACHE_KEY, JSON.stringify(list));
  } catch {
    // localStorage indisponível (aba anônima/quota) — cache é só otimização.
  }
}

export function clearUsersListCache(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(USERS_LIST_CACHE_KEY);
  } catch {
    /* noop */
  }
}

// Integração real com a API oficial do Traccar (GET/POST/PUT/DELETE
// /api/users) — mesmo padrão de vehicles.service.ts/commands.service.ts:
// sempre com credentials: "include", sem mock/fallback.
export const usersService = {
  list: async () => {
    // Isolamento entre empresas (2026-10-16): admin da empresa principal é
    // administrator do Traccar, que devolve TODOS os usuários — tira os de
    // outra empresa (criada no Super Master). Se o backend não responder,
    // segue sem filtro (nunca deixa a tela vazia por isso).
    const [all, foreign] = await Promise.all([
      apiRequest<User[]>("/api/users", { method: "GET", credentials: "include" }),
      gpsDriveGet<number[]>("/api/v1/users/other-companies/traccar-ids").catch(() => [] as number[]),
    ]);
    const hidden = new Set(foreign);
    const list = hidden.size ? all.filter((u) => !hidden.has(u.id)) : all;
    writeUsersListCache(list);
    return list;
  },
  get: (id: number) =>
    apiRequest<User>(`/api/users/${id}`, { method: "GET", credentials: "include" }),
  create: (data: Partial<User>) =>
    apiRequest<User>("/api/users", { method: "POST", credentials: "include", body: data }),
  update: (id: number, data: Partial<User>) =>
    apiRequest<User>(`/api/users/${id}`, { method: "PUT", credentials: "include", body: data }),
  remove: (id: number) =>
    apiRequest<void>(`/api/users/${id}`, { method: "DELETE", credentials: "include" }),
};
